/**
 * Phase 4 — global connectivity banner.
 *
 * Renders nothing while online and the queue is empty. When offline OR
 * there are queued intents pending, surfaces a thin coloured pill at
 * the top of the viewport. Designed to be mounted ONCE at the App root.
 *
 * The banner is intentionally tiny (one line of text) so it doesn't
 * push real content around — the dashboards already use the full
 * viewport.
 */
import React, { useEffect, useState } from 'react';
import { useConnectivity } from '@/lib/connectivity';
import { listQueuedIntents, flushOfflineQueue } from '@/lib/offline-axios';
import { onUpdateAvailable, applyUpdateAndReload } from '@/lib/sw-register';

const POLL_INTERVAL_MS = 4000;

export default function ConnectivityBanner() {
  const { online } = useConnectivity();
  const [queueLen, setQueueLen] = useState(0);
  const [flushing, setFlushing] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // Cheap poll — IndexedDB read every 4s. The queue rarely has more
  // than a handful of entries, so the cost is negligible.
  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const items = await listQueuedIntents();
        if (active) setQueueLen(items.length);
      } catch (_) { /* IDB not ready yet, fine */ }
    };
    refresh();
    const t = setInterval(refresh, POLL_INTERVAL_MS);
    return () => { active = false; clearInterval(t); };
  }, [online]);

  // Subscribe to "a new app version is ready" — the SW lifecycle
  // (registration.update() polled every 10 min + on visibilitychange)
  // fires this when a fresh service worker has installed and is
  // waiting to take control.
  useEffect(() => {
    const unsub = onUpdateAvailable(({ available }) => {
      if (available) setUpdateAvailable(true);
    });
    return unsub;
  }, []);

  const handleRetry = async () => {
    setFlushing(true);
    try {
      await flushOfflineQueue();
    } finally {
      setFlushing(false);
    }
  };

  const handleUpdate = () => {
    applyUpdateAndReload();
  };

  // New-version banner takes precedence over the offline / queue
  // banner — the user might be queueing onto stale code right now.
  if (updateAvailable) {
    return (
      <div
        data-testid="connectivity-banner"
        style={{
          position:   'fixed',
          top:        0, left: 0, right: 0,
          zIndex:     9999,
          padding:    '6px 12px',
          fontSize:   13,
          textAlign:  'center',
          background: '#0d4a7a',
          color:      '#fff',
        }}
      >
        <span data-testid="connectivity-state">A new version of Mojo Money is ready.</span>{' '}
        <button
          type="button"
          onClick={handleUpdate}
          data-testid="connectivity-update-btn"
          style={{
            marginLeft: 8, background: '#fff', color: '#0d4a7a',
            border: 'none', padding: '2px 10px', borderRadius: 4,
            cursor: 'pointer', fontWeight: 600,
          }}
        >Refresh now</button>
      </div>
    );
  }

  if (online && queueLen === 0) return null;

  const offlineStyle = {
    background: '#7a3b00',
    color:      '#fff',
  };
  const queuedStyle = {
    background: '#0a6e4a',
    color:      '#fff',
  };

  return (
    <div
      data-testid="connectivity-banner"
      style={{
        position:    'fixed',
        top:         0, left: 0, right: 0,
        zIndex:      9999,
        padding:     '6px 12px',
        fontSize:    13,
        textAlign:   'center',
        ...(online ? queuedStyle : offlineStyle),
      }}
    >
      {online
        ? (
          <>
            <span data-testid="connectivity-state">Back online — </span>
            <span data-testid="queue-length">{queueLen}</span>{' '}pending {queueLen === 1 ? 'transaction' : 'transactions'} in the offline queue.{' '}
            <button
              type="button"
              onClick={handleRetry}
              disabled={flushing}
              data-testid="connectivity-retry-btn"
              style={{
                marginLeft: 8, background: 'transparent',
                color: '#fff', border: '1px solid #fff',
                padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
                opacity: flushing ? 0.5 : 1,
              }}
            >
              {flushing ? 'Sending…' : 'Send now'}
            </button>
          </>
        )
        : (
          <>
            <span data-testid="connectivity-state">You're offline</span> — viewing last-synced data.
            {queueLen > 0 && (
              <span>{' '}<span data-testid="queue-length">{queueLen}</span> queued for when you reconnect.</span>
            )}
          </>
        )
      }
    </div>
  );
}
